import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, Sparkles, Download, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGov } from "../../governance/GovContext";
import { EXPOSURE_QUESTIONS, SCORED_QUESTIONS, ANSWERS, GOV_DISCLAIMER_ASSESS, SKJOLD_OPTIONS } from "../../governance/govData";
import { savePdf } from "../../governance/govPdf";
import { createReport } from "../../governance/govShare";
import { Button } from "../ui/button";
import Radar from "./Radar";

const bandTone = { red: "text-signal-red border-signal-red/30 bg-signal-red/10", amber: "text-signal-amber border-signal-amber/30 bg-signal-amber/10", cyan: "text-primary border-primary/30 bg-primary/10", green: "text-signal-green border-signal-green/30 bg-signal-green/10" };

function QRow({ q, value, onChange }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-sm text-foreground">{q.text}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {ANSWERS.map((a) => (
          <button key={a.value} onClick={() => onChange(q.id, a.value)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${value === a.value ? "border-primary/50 bg-primary/12 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReadinessAssessment({ onGoto }) {
  const gov = useGov();
  const { answers, exposure, setAnswer, setExposure, result } = gov;
  const { skjold, setSkjold } = gov;
  const [sharing, setSharing] = useState(false);

  const share = async () => {
    setSharing(true);
    try {
      const { url } = await createReport(gov);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Private link created and copied to clipboard");
    } catch (e) {
      toast.error("Could not create link. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Context</p>
        {EXPOSURE_QUESTIONS.map((q) => <QRow key={q.id} q={q} value={exposure[q.id]} onChange={setExposure} />)}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="skjold-status-control">
          <p className="text-sm text-foreground">Is Skjold (document guard) in place to stop staff sharing restricted documents with AI tools?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {SKJOLD_OPTIONS.map((o) => (
              <button key={o.value} onClick={() => setSkjold(o.value)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${skjold === o.value ? "border-primary/50 bg-primary/12 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground pt-2">Governance controls</p>
        {SCORED_QUESTIONS.map((q) => <QRow key={q.id} q={q} value={answers[q.id]} onChange={setAnswer} />)}
      </div>

      <div className="lg:sticky lg:top-32 lg:self-start">
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          {!result ? (
            <div className="text-center py-10">
              <Sparkles className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-3 text-sm text-muted-foreground">Answer the governance questions to see your maturity profile.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Governance maturity</p>
                  <span className={`mt-1 inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${bandTone[result.band.tone]}`}>{result.band.band}</span>
                </div>
                <div className="text-right"><p className="font-heading text-3xl font-bold text-foreground">{result.overall}</p><p className="text-[0.7rem] text-muted-foreground">index / 100</p></div>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{result.band.blurb}</p>
              <Radar values={result.dims} />
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="text-xs font-semibold text-signal-green flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Strongest</p>
                  <ul className="mt-1.5 space-y-1 text-sm text-muted-foreground">{result.strongest.length ? result.strongest.map((d) => <li key={d}>{d}</li>) : <li>—</li>}</ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-signal-amber flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Gaps</p>
                  <ul className="mt-1.5 space-y-1 text-sm text-muted-foreground">{result.gaps.length ? result.gaps.map((d) => <li key={d}>{d}</li>) : <li>—</li>}</ul>
                </div>
              </div>

              {result.gaps.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Suggested next actions</p>
                  {result.gaps.map((d) => (
                    <div key={d} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                      <p className="text-sm text-foreground">{result.support[d].action}</p>
                      <p className="mt-1 text-xs text-primary">norse3: {result.support[d].norse3}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {result.gaps.includes("Monitoring") && <Button size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => onGoto && onGoto("qa")}>Model your QA coverage</Button>}
                {result.gaps.includes("Policy") && <Button size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => onGoto && onGoto("policy")}>Build a policy</Button>}
                {(result.gaps.includes("Data Protection") || result.gaps.includes("Intervention")) && <Button size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => onGoto && onGoto("deploy")}>Explore deployment</Button>}
              </div>

              <Button asChild className="mt-4 w-full bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Discuss your governance model <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
              <Button variant="outline" onClick={() => savePdf.assessment(result, answers, exposure)} data-testid="assessment-download-pdf" className="mt-2.5 w-full border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>
              <Button variant="outline" onClick={share} disabled={sharing} data-testid="assessment-share-link" className="mt-2.5 w-full border-white/15 bg-transparent hover:bg-white/5 text-foreground">{sharing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />} Share private link</Button>
              <p className="mt-3 text-xs text-muted-foreground/80">{GOV_DISCLAIMER_ASSESS}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
